import { Fragment } from 'react';
import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { newsTranslationRef } from '../../translations';
import { newsApiRef } from '../../api';
import { InfoCard, Progress, ResponseErrorPanel, ErrorBoundary } from '@backstage/core-components';
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Divider,
  Button,
  Box,
} from '@material-ui/core';
import NewspaperIcon from '@material-ui/icons/Description';
import { Link } from 'react-router-dom';
import { decodeHtmlEntities } from '../../utils/htmlEntities';

export const LatestNewsCard = () => {
  const api = useApi(newsApiRef);
  const { t } = useTranslationRef(newsTranslationRef);

  const { value, loading, error } = useAsync(async () => {
    const result = await api.getPublishedArticlesPaginated(1, 3);
    return result.articles || [];
  }, [api]);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const articles = value || [];

  return (
    <ErrorBoundary>
      <InfoCard title={t('latestNewsCard.title')}>
        <List dense>
          {articles.map((article, index) => (
            <Fragment key={article.id ?? index}>
              <ListItem button component={Link} to={`/news/${article.id}`}>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: '#3B7BBE' }}>
                    <NewspaperIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={decodeHtmlEntities(article.title)}
                  secondary={`${article.on_behalf_of || article.author} • ${(() => {
                    const dateToShow = article.updated_at || article.created_at;
                    return dateToShow
                      ? new Date(dateToShow).toLocaleDateString(t('common.locale'), {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : '';
                  })()}`}
                />
              </ListItem>
              {index < articles.length - 1 && <Divider variant="inset" component="li" />}
            </Fragment>
          ))}
          {articles.length === 0 && (
            <Typography
              variant="body2"
              style={{
                padding: '16px',
                textAlign: 'center',
                color: 'gray',
                fontStyle: 'italic',
              }}
            >
              {t('latestNewsCard.noNews')}
            </Typography>
          )}
        </List>
        {articles.length > 0 && (
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button component={Link} to="/news" color="primary" size="small">
              {t('latestNewsCard.showAll')}
            </Button>
          </Box>
        )}
      </InfoCard>
    </ErrorBoundary>
  );
};
